import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  ArrowLeft,
  Plus, 
  Users, 
  Mail,
  Phone,
  Link2,
  Briefcase,
  Upload,
  FileText,
  Send,
  Copy,
  ExternalLink,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Download,
  FileSpreadsheet
} from "lucide-react";
import { toast } from "sonner";
import { interviewLanguages } from "../i18n";

const API = process.env.REACT_APP_BACKEND_URL;

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  
  // Forms
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    phone: '',
    cv_text: ''
  });
  const [cvFile, setCvFile] = useState(null);
  
  const [linkForm, setLinkForm] = useState({
    interview_language: 'fr',
    is_bilingual: false,
    secondary_language: '',
    validity_period: '7d',
    difficulty_level: 'medium',
    send_email: true
  });

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [userRes, jobRes, candidatesRes] = await Promise.all([
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
        fetch(`${API}/api/jobs/${jobId}`, { credentials: 'include' }),
        fetch(`${API}/api/candidates?job_id=${jobId}`, { credentials: 'include' })
      ]);

      if (userRes.ok) setUser(await userRes.json());
      if (jobRes.ok) setJob(await jobRes.json());
      if (candidatesRes.ok) {
        const candidatesList = await candidatesRes.json();
        // Fetch interview links for each candidate
        const candidatesWithLinks = await Promise.all(
          candidatesList.map(async (candidate) => {
            const linksRes = await fetch(
              `${API}/api/interview-links?candidate_id=${candidate.candidate_id}`,
              { credentials: 'include' }
            );
            const links = linksRes.ok ? await linksRes.json() : [];
            return { ...candidate, interview_links: links };
          })
        );
        setCandidates(candidatesWithLinks);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleCvFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error("Format non supporté. Utilisez PDF ou Word.");
      return;
    }
    
    setCvFile(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/extract-document`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setCandidateForm(prev => ({ ...prev, cv_text: data.text || '' }));
        toast.success("CV importé avec succès");
      } else {
        toast.error("Erreur lors de l'extraction du CV");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors de l'upload");
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Le fichier doit être au format CSV");
      return;
    }
    
    setImportLoading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/candidates/import-csv?job_id=${jobId}`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`${data.candidates_created} candidat(s) importé(s) avec succès`);
        if (data.errors && data.errors.length > 0) {
          toast.warning(`${data.errors.length} erreur(s) lors de l'import`);
        }
        setImportDialogOpen(false);
        fetchData();
      } else {
        toast.error(data.detail || "Erreur lors de l'import");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors de l'import CSV");
    } finally {
      setImportLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetch(`${API}/api/candidates/export-csv?job_id=${jobId}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `candidats_${job?.title || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(t('success.exported'));
      } else {
        const error = await res.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleCreateCandidate = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API}/api/candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...candidateForm,
          job_id: jobId
        })
      });

      if (res.ok) {
        toast.success("Candidat créé avec succès");
        setCandidateDialogOpen(false);
        setCandidateForm({ name: '', email: '', phone: '', cv_text: '' });
        setCvFile(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors de la création");
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    
    if (!selectedCandidate) {
      toast.error("Veuillez sélectionner un candidat");
      return;
    }
    
    // Check if candidate already has a link
    if (selectedCandidate.interview_links && selectedCandidate.interview_links.length > 0) {
      toast.error("Ce candidat a déjà un lien d'entretien");
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/interview-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_id: jobId,
          candidate_ids: [selectedCandidate.candidate_id],
          link_type: 'individual',
          ...linkForm
        })
      });

      if (res.ok) {
        toast.success("Lien d'entretien créé avec succès");
        setLinkDialogOpen(false);
        setSelectedCandidate(null);
        setLinkForm({
          interview_language: 'fr',
          is_bilingual: false,
          secondary_language: '',
          validity_period: '7d',
          difficulty_level: 'medium',
          send_email: true
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors de la création");
    }
  };

  const copyLink = (link) => {
    const url = `${window.location.origin}/interview/${link.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié dans le presse-papier");
  };

  const openLinkDialog = (candidate) => {
    if (candidate.interview_links && candidate.interview_links.length > 0) {
      toast.error("Ce candidat a déjà un lien d'entretien. Un seul lien par candidat.");
      return;
    }
    setSelectedCandidate(candidate);
    setLinkDialogOpen(true);
  };

  const getStatusBadge = (link) => {
    if (!link) return null;
    
    const isUsed = link.status === 'expired' && link.expired_reason === 'interview_completed';
    const isDisqualified = link.status === 'expired' && link.expired_reason === 'candidate_disqualified';
    
    if (isUsed || isDisqualified) {
      return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />{t('jobDetail.linkUsed')}</Badge>;
    }
    
    switch (link.status) {
      case 'active':
        return <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50"><Link2 className="w-3 h-3 mr-1" />{t('jobDetail.linkCreated')}</Badge>;
      case 'expired':
        return <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50"><AlertCircle className="w-3 h-3 mr-1" />{t('jobDetail.linkExpired')}</Badge>;
      default:
        return <Badge variant="outline">{link.status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas">
        <Navbar user={user} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-canvas">
        <Navbar user={user} />
        <div className="max-w-7xl mx-auto px-6 py-8">
          <p>Poste non trouvé</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button & Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/jobs')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('jobDetail.backToJobs')}
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-ink">{job.title}</h1>
                  <p className="text-ink-muted">{job.department} • {job.location}</p>
                </div>
              </div>
            </div>
            <Badge className={job.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-700'}>
              {job.status === 'active' ? t('jobs.status.active') : t('jobs.status.inactive')}
            </Badge>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ink-muted">{t('jobDetail.candidates')}</p>
                  <p className="text-2xl font-bold text-ink">{candidates.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ink-muted">{t('jobDetail.linksCreated')}</p>
                  <p className="text-2xl font-bold text-ink">
                    {candidates.filter(c => c.interview_links?.length > 0).length}
                  </p>
                </div>
                <Link2 className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ink-muted">{t('jobDetail.completedInterviews')}</p>
                  <p className="text-2xl font-bold text-ink">
                    {candidates.filter(c => 
                      ['passed', 'not_recommended', 'rejected', 'failed'].includes(c.status)
                    ).length}
                  </p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Candidates Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('jobDetail.candidates')} ({candidates.length})
            </CardTitle>
            <div className="flex gap-2">
              {candidates.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={handleExportCsv} 
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('jobDetail.exportCsv')}
                </Button>
              )}
              <Button 
                variant="outline" 
                onClick={() => setImportDialogOpen(true)} 
                data-testid="import-csv-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {t('jobDetail.importCsv')}
              </Button>
              <Button onClick={() => setCandidateDialogOpen(true)} data-testid="add-candidate-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('jobDetail.addCandidate')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {candidates.length === 0 ? (
              <div className="text-center py-12 text-ink-muted">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('jobDetail.noCandidates')}</p>
                <p className="text-sm">{t('jobDetail.noCandidatesDesc')}</p>
              </div>
            ) : (
              <Accordion type="multiple" className="space-y-2">
                {candidates.map((candidate) => (
                  <AccordionItem 
                    key={candidate.candidate_id} 
                    value={candidate.candidate_id}
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                            {candidate.name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-ink">{candidate.name}</p>
                            <p className="text-sm text-ink-muted">{candidate.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {candidate.interview_links && candidate.interview_links.length > 0 ? (
                            getStatusBadge(candidate.interview_links[0])
                          ) : (
                            <Badge variant="outline" className="text-zinc-500">{t('jobDetail.noLink')}</Badge>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <div className="pl-14 space-y-4">
                        {/* Contact Info */}
                        <div className="flex gap-6 text-sm text-ink-muted">
                          {candidate.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {candidate.email}
                            </span>
                          )}
                          {candidate.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {candidate.phone}
                            </span>
                          )}
                        </div>

                        {/* CV Preview */}
                        {candidate.cv_text && (
                          <div className="bg-zinc-50 rounded-lg p-3">
                            <p className="text-xs text-ink-muted mb-1 font-medium">CV:</p>
                            <p className="text-sm text-ink line-clamp-3">{candidate.cv_text}</p>
                          </div>
                        )}

                        {/* Interview Link Section */}
                        <div className="border-t pt-4">
                          <p className="text-sm font-medium text-ink mb-3">{t('jobDetail.interviewLink')}</p>
                          
                          {candidate.interview_links && candidate.interview_links.length > 0 ? (
                            <div className="bg-blue-50 rounded-lg p-4">
                              {candidate.interview_links.map((link) => (
                                <div key={link.link_id} className="flex items-center justify-between">
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      {getStatusBadge(link)}
                                      <span className="text-xs text-ink-muted">
                                        {t('jobDetail.createdOn')} {new Date(link.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <p className="text-sm text-ink-muted">
                                      Langue: {interviewLanguages.find(l => l.code === link.interview_language)?.name || link.interview_language}
                                      {link.is_bilingual && ` + ${interviewLanguages.find(l => l.code === link.secondary_language)?.name}`}
                                    </p>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => copyLink(link)}
                                    >
                                      <Copy className="w-4 h-4 mr-1" />
                                      {t('jobDetail.copy')}
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => window.open(`/interview/${link.token}`, '_blank')}
                                    >
                                      <ExternalLink className="w-4 h-4 mr-1" />
                                      {t('jobDetail.open')}
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Button 
                              onClick={() => openLinkDialog(candidate)}
                              className="w-full"
                              variant="outline"
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Créer un lien d'entretien
                            </Button>
                          )}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Candidate Dialog */}
      <Dialog open={candidateDialogOpen} onOpenChange={setCandidateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un candidat</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCandidate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                value={candidateForm.name}
                onChange={(e) => setCandidateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Jean Dupont"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={candidateForm.email}
                onChange={(e) => setCandidateForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="jean@example.com"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={candidateForm.phone}
                onChange={(e) => setCandidateForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+33 6 12 34 56 78"
              />
            </div>
            
            <div className="space-y-2">
              <Label>CV</Label>
              <label className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleCvFileUpload}
                  className="hidden"
                />
                <Upload className="w-4 h-4 mr-2 text-slate-500" />
                <span className="text-sm text-slate-600">
                  {cvFile ? cvFile.name : "Importer CV (PDF ou Word)"}
                </span>
              </label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={candidateForm.cv_text}
                onChange={(e) => setCandidateForm(prev => ({ ...prev, cv_text: e.target.value }))}
                placeholder="Ou collez le contenu du CV ici..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCandidateDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">Créer le candidat</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Interview Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un lien d'entretien</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <form onSubmit={handleCreateLink} className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm font-medium text-ink">Candidat sélectionné:</p>
                <p className="text-ink">{selectedCandidate.name}</p>
                <p className="text-sm text-ink-muted">{selectedCandidate.email}</p>
              </div>
              
              <div className="space-y-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <Label htmlFor="bilingual-switch" className="text-sm cursor-pointer font-medium">
                    Mode bilingue
                  </Label>
                  <Switch 
                    id="bilingual-switch"
                    checked={linkForm.is_bilingual} 
                    onCheckedChange={(v) => setLinkForm(prev => ({ ...prev, is_bilingual: v }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-ink-muted">
                    {linkForm.is_bilingual ? 'Langue principale' : "Langue de l'entretien"}
                  </Label>
                  <Select
                    value={linkForm.interview_language}
                    onValueChange={(v) => setLinkForm(prev => ({ ...prev, interview_language: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {interviewLanguages.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {linkForm.is_bilingual && (
                  <div className="space-y-2">
                    <Label className="text-xs text-ink-muted">Langue secondaire</Label>
                    <Select
                      value={linkForm.secondary_language}
                      onValueChange={(v) => setLinkForm(prev => ({ ...prev, secondary_language: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir une langue..." />
                      </SelectTrigger>
                      <SelectContent>
                        {interviewLanguages
                          .filter(l => l.code !== linkForm.interview_language)
                          .map(lang => (
                            <SelectItem key={lang.code} value={lang.code}>
                              {lang.flag} {lang.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Validité du lien</Label>
                  <Select
                    value={linkForm.validity_period}
                    onValueChange={(v) => setLinkForm(prev => ({ ...prev, validity_period: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24h">24 heures</SelectItem>
                      <SelectItem value="7d">7 jours</SelectItem>
                      <SelectItem value="30d">30 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <p className="text-xs text-ink-muted">
                Durée: 15-30 min selon l'appréciation de l'agent
              </p>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={linkForm.send_email}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, send_email: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="send_email" className="cursor-pointer">
                  Envoyer une invitation par email
                </Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit">
                  <Send className="w-4 h-4 mr-2" />
                  Créer le lien
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Importer des candidats
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
              <p className="font-medium mb-2">Format du fichier CSV :</p>
              <p>Colonnes attendues : <code className="bg-blue-100 px-1 rounded">nom</code>, <code className="bg-blue-100 px-1 rounded">email</code>, <code className="bg-blue-100 px-1 rounded">telephone</code> (optionnel)</p>
              <p className="mt-2 text-xs">Séparateur : virgule (,) ou point-virgule (;)</p>
            </div>
            
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvImport}
                className="hidden"
                id="csv-import"
                disabled={importLoading}
              />
              <label htmlFor="csv-import" className="cursor-pointer">
                {importLoading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                    <p className="text-sm text-ink-muted">Import en cours...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-10 h-10 text-slate-400" />
                    <p className="text-sm font-medium text-ink">Cliquez pour sélectionner un fichier CSV</p>
                    <p className="text-xs text-ink-muted">ou glissez-déposez</p>
                  </div>
                )}
              </label>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Download className="w-4 h-4" />
              <a 
                href={`data:text/csv;charset=utf-8,nom,email,telephone%0AJean Dupont,jean@example.com,+33612345678%0AMarie Martin,marie@example.com,`}
                download="template_candidats.csv"
                className="text-blue-600 hover:underline"
              >
                Télécharger un modèle CSV
              </a>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
